import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { TooltipContainer, TooltipContent } from './InfoTooltip.styles'

export type IconTooltipProps = {
  icon: React.ReactNode
  tooltipText: string
}

export function IconTooltip({ icon, tooltipText }: IconTooltipProps): JSX.Element {
  const [isVisible, setIsVisible] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isVisible) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsVisible(false)
    }

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsVisible(false)
      }
    }

    document.addEventListener('keydown', handleEscape)
    document.addEventListener('click', handleClickOutside, true)

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.removeEventListener('click', handleClickOutside, true)
    }
  }, [isVisible])

  useEffect(() => {
    if (!isVisible || !containerRef.current) return

    const updatePosition = () => {
      if (!containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      setPosition({
        top: rect.top + rect.height / 2,
        left: rect.right + 10,
      })
    }

    updatePosition()
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)

    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [isVisible])

  return (
    <>
      <TooltipContainer
        ref={containerRef}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}>
        {icon}
      </TooltipContainer>
      {isVisible &&
        createPortal(
          <TooltipContent $top={position.top} $left={position.left}>
            {tooltipText}
          </TooltipContent>,
          document.body,
        )}
    </>
  )
}
